import { Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import {
  TiArrowSortedDown,
  TiArrowSortedUp,
  TiEquals,
  TiEye,
} from "react-icons/ti";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import { NumericFormat } from "react-number-format";
import { usePagination, useTable } from "react-table";
import _ from "../../../i18n";
import { getTimeByDurationFormat, toFixed } from "../../../utils";
import { InfoCircleFilledIcon } from "../../common/Icons";
import { EntityIcon } from "../../common/Icons/EntityIcon";
import TTPPaginator from "../../common/TTPPaginator";
import "./TreeTable.local.scss";
import GetRiskIcon from "../../common/Icons/getRiskIcon";

export default function TreeTable({
  data,
  erpData,
  nbResults,
  filter,
  setFilter,
}) {
  const { page: paginationPage, pageSize } = filter;
  const [sortedData, setSortedData] = useState(null);
  const [sortBy, setSortBy] = useState({
    budgetHr: null,
    prestaTs: null,
    budgetEco: null,
    turnover: null,
    minBilling: null,
    encours: null,
  });

  const calculateRiskLevel = (encoursData) => {
    if (!encoursData || !encoursData.subTotals) return null;
    
    const { up90Days, up60Days } = encoursData.subTotals;
    
    if (up90Days > 0) return 'HIGH';
    if (up60Days > 0) return 'MEDIUM';
    return 'LOW';
  };

  useEffect(() => {
    if (data) {
      setSortedData(data);
    }
  }, [data]);

  useEffect(() => {
    if (!data?.result?.length) return;

    const sortedRows = data?.result
      .filter((client) => client.client_id !== "%")
      .sort((a, b) => {
        const sortKeys = Object.keys(sortBy);

        for (let key of sortKeys) {
          const sortOrder = sortBy[key];
          if (!sortOrder) continue;

          let valueA, valueB;

          if (key === "budgetHr") {
            valueA = a.data?.budgetH["total"];
            valueB = b.data?.budgetH["total"];
          } else if (key === "prestaTs") {
            valueA = a.data?.PrestaTs["total"];
            valueB = b.data?.PrestaTs["total"];
          } else if (key === "budgetEco") {
            valueA = a.data?.budgetEco["total"];
            valueB = b.data?.budgetEco["total"];
          } else if (key === "turnover") {
            valueA = a.data?.profit["revenue"];
            valueB = b.data?.profit["revenue"];
          } else if (key === "minBilling") {
            valueA = a.data?.minBilling["total"];
            valueB = b.data?.minBilling["total"];
          } else if (key === "encours") {
            valueA = a.data?.Encours?.["total"];
            valueB = b.data?.Encours?.["total"];
          }

          valueA = valueA === "-" ? null : parseFloat(valueA);
          valueB = valueB === "-" ? null : parseFloat(valueB);

          if (isNaN(valueA)) valueA = null;
          if (isNaN(valueB)) valueB = null;

          if (valueA === null && valueB === null) continue;
          if (valueA === null) return sortOrder === "ASC" ? 1 : -1;
          if (valueB === null) return sortOrder === "ASC" ? -1 : 1;

          const comparison = valueA - valueB;
          if (comparison !== 0) {
            return sortOrder === "ASC" ? comparison : -comparison;
          }
        }

        return 0;
      });

    setSortedData({
      ...data,
      result: sortedRows,
    });
  }, [sortBy]);

  const handleSort = (key) => {
    setSortBy((prev) => {
      const newSort = {};
      Object.keys(prev).forEach((k) => {
        newSort[k] = null;
      });

      newSort[key] = prev[key] === "ASC" ? "DESC" : "ASC";
      return newSort;
    });
  };

  const columns = useMemo(
    () => [
      {
        id: "client",
        Header: "Client",
        accessor: (row) => row["data"],
        Cell: ({ value, row }) => {
          const {
            id,
            label,
            client_recovr_external_id,
            attachedClients,
            diff,
          } = value;
          const encoursData = row.original.data?.Encours;
          const riskLevel = calculateRiskLevel(encoursData);          
          return (
            <div
              key={id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ flexShrink: 0 }}>
                  <GetRiskIcon riskLevel={riskLevel} size={18} />
                </div>
                {label.length > 35 ? (
                  <Tooltip placement="right" title={label} mouseLeaveDelay={0}>
                    <p style={{ fontSize: "14px", margin: 0 }}>{`${label.substring(
                      0,
                      35
                    )}...`}</p>
                  </Tooltip>
                ) : (
                  <p style={{ fontSize: "14px", margin: 0 }}>{label}</p>
                )}
              </div>    
              <div className="import-info">
                <div>
                  {/* {diff >= 5 ? (
                    <Tooltip placement="top" title={_("suspect")}>
                      <div className="suspect-badge">
                        <TiEye color="#de4848" />
                      </div>
                    </Tooltip>
                  ) : (
                    ""
                  )} */}
                  {attachedClients && attachedClients.length ? (
                    <Tooltip
                      placement="top"
                      title={
                        <div
                          style={{
                            textAlign: "left",
                          }}
                        >
                          <p>
                            {_("attachedClients")}
                            {" : "}
                          </p>
                          {attachedClients.map((item) => (
                            <p>{"- " + item.clientName}</p>
                          ))}
                        </div>
                      }
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <EntityIcon size={14} color={"#6d7f92"} />
                      </div>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                  {client_recovr_external_id ? (
                    <a href={client_recovr_external_id} target="_blank" rel="noopener noreferrer">
                      <Tooltip
                        placement="top"
                        title={
                          <div
                            style={{
                              textAlign: "left",
                            }}
                          >
                            Portail client
                          </div>
                        }
                      >
                        <img
                          src="https://static.intercomassets.com/avatars/6472131/square_128/Group_39910-1744282782.png"
                          alt="recovr_logo"
                          width={14}
                        />
                      </Tooltip>
                    </a>
                  ) : (
                    ""
                  )}
                  {erpData &&
                    erpData.clients.map((erp, index) => {
                      return erp[id] && erp[id].erpId ? (
                        <Tooltip
                          key={index}
                          placement="top"
                          title={
                            <div>
                              {erp[id].erpLabel && (
                                <p>
                                  {_("importedFrom")} : {erp[id].erpLabel}
                                </p>
                              )}

                              <p>
                                {_("identifier")} : {erp[id].erpId}
                              </p>
                            </div>
                          }
                        >
                          <InfoCircleFilledIcon size={14} color="#6d7f92" />
                        </Tooltip>
                      ) : (
                        ""
                      );
                    })}
                </div>
              </div>
            </div>
          );
        },
      },

      {
        Header: "Horaire",
        columns: [
          {
            id: "budget",
            Header: (
              <div className="col" onClick={() => handleSort("budgetHr")}>
                Budget
                <div className="sortBy_arrows">
                  <FaArrowDown
                    fill={
                      sortBy["budgetHr"] == "DESC" ? "#18a0fb" : "#18a0fb50"
                    }
                  />
                  <FaArrowUp
                    fill={sortBy["budgetHr"] == "ASC" ? "#18a0fb" : "#18a0fb50"}
                  />
                </div>
              </div>
            ),
            accessor: (row) => row["data"]["budgetH"],
            Cell: ({ value }) =>
              value && (
                <div>
                  <span
                    style={{
                      marginBottom: "0px",
                      textAlign: "center",
                    }}
                  >
                    <p style={{ fontSize: "14px" }}>
                      {getTimeByDurationFormat(value["total"])}
                    </p>
                  </span>
                  <div
                    className="zone-totals"
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                    }}
                  >
                    {value["subTotals"] &&
                      value["subTotals"].map((item) => (
                        <div key={item.zone} style={{ fontSize: "12px" }}>
                          <span>{item.zone}:</span>
                          {getTimeByDurationFormat(toFixed(item.value, 2))}
                        </div>
                      ))}
                  </div>
                </div>
              ),
          },
          {
            id: "time",
            Header: (
              <div className="col" onClick={() => handleSort("prestaTs")}>
                Réalisé
                <div className="sortBy_arrows">
                  <FaArrowDown
                    fill={
                      sortBy["prestaTs"] == "DESC" ? "#18a0fb" : "#18a0fb50"
                    }
                  />
                  <FaArrowUp
                    fill={sortBy["prestaTs"] == "ASC" ? "#18a0fb" : "#18a0fb50"}
                  />
                </div>
              </div>
            ),
            accessor: (row) => row["data"]["PrestaTs"],
            Cell: ({ value }) =>
              value && (
                <div>
                  <div className="cell_container_budget_hr">
                    <span
                      style={{
                        marginBottom: "0px",
                        textAlign: "center",
                      }}
                    >
                      <p style={{ fontSize: "14px" }}>
                        {getTimeByDurationFormat(value["total"])}
                      </p>
                    </span>
                    <span
                      className="diff-badge"
                      style={{
                        "--color": value["diff"] <= 0 ? "#02af8e" : "#de4848",
                        "--bg-color":
                          value["diff"] <= 0 ? "#02af8e25" : "#de484825",
                      }}
                    >
                      <NumericFormat
                        value={value["diff"]}
                        displayType={"text"}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        suffix=" %"
                      />
                    </span>
                  </div>
                  <div
                    className="zone-totals"
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                    }}
                  >
                    {value["subTotals"] &&
                      value["subTotals"].map((item) => (
                        <div key={item.zone} style={{ fontSize: "12px" }}>
                          <span>{item.zone}:</span>
                          {getTimeByDurationFormat(toFixed(item.value, 2))}
                        </div>
                      ))}
                  </div>
                </div>
              ),
          },
        ],
      },

      {
        Header: "Economique",
        columns: [
          {
            id: "economic",
            Header: (
              <div className="col" onClick={() => handleSort("budgetEco")}>
                Budget
                <div className="sortBy_arrows">
                  <FaArrowDown
                    fill={
                      sortBy["budgetEco"] == "DESC" ? "#18a0fb" : "#18a0fb50"
                    }
                  />
                  <FaArrowUp
                    fill={
                      sortBy["budgetEco"] == "ASC" ? "#18a0fb" : "#18a0fb50"
                    }
                  />
                </div>
              </div>
            ),
            accessor: (row) => row["data"]["budgetEco"],
            Cell: ({ value, row: { index }, column: { id } }) =>
              value && (
                <div className="cell_container">
                  <NumericFormat
                    value={value["total"].toFixed(2)}
                    displayType={"text"}
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    suffix=" €"
                  />
                  <div className="sub_cell">
                    <p>{`${_("budgetRate")}: `}</p>
                    <NumericFormat
                      value={value["ratio"]}
                      displayType={"text"}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      suffix="€/h"
                    />
                  </div>
                </div>
              ),
          },
          {
            id: "profit",
            Header: (
              <div className="col" onClick={() => handleSort("turnover")}>
                Réalisé
                <div className="sortBy_arrows">
                  <FaArrowDown
                    fill={
                      sortBy["turnover"] == "DESC" ? "#18a0fb" : "#18a0fb50"
                    }
                  />
                  <FaArrowUp
                    fill={sortBy["turnover"] == "ASC" ? "#18a0fb" : "#18a0fb50"}
                  />
                </div>
              </div>
            ),
            accessor: (row) => row["data"]["profit"],
            Cell: ({ value }) =>
              value && (
                <div className="cell_container">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <NumericFormat
                      value={value["revenue"].toFixed(2)}
                      displayType={"text"}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      suffix=" €"
                    />

                    {value["diff"] > 0 ? (
                      <TiArrowSortedUp color="#02af8e" size={15} />
                    ) : value["diff"] < 0 ? (
                      <TiArrowSortedDown color="#de4848" size={15} />
                    ) : (
                      <TiEquals color="#ea8124" size={12} />
                    )}
                  </div>
                  <div className="sub_cell">
                    <p>{`${_("effectifRate")}: `}</p>
                    <NumericFormat
                      value={value["ratio"]}
                      displayType={"text"}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      suffix="€/h"
                    />
                  </div>
                </div>
              ),
          },
        ],
      },

      {
        id: "minBilling",
        Header: (
          <div className="col" onClick={() => handleSort("minBilling")}>
            {_("minimumBilling")}
            <div className="sortBy_arrows">
              <FaArrowDown
                fill={sortBy["minBilling"] == "DESC" ? "#18a0fb" : "#18a0fb50"}
              />
              <FaArrowUp
                fill={sortBy["minBilling"] == "ASC" ? "#18a0fb" : "#18a0fb50"}
              />
            </div>
          </div>
        ),
        accessor: (row) => row["data"]["minBilling"],
        Cell: ({ value }) =>
          value && (
            <div className="cell_container">
              <NumericFormat
                value={Math.round(value["total"])}
                displayType={"text"}
                thousandSeparator={"."}
                decimalSeparator={","}
                suffix=" €"
              />
            </div>
          ),
      },

      {
        Header: "Encours",
        columns: [
          {
            id: "total",
            Header: (
              <div className="col" onClick={() => handleSort("encours")}>
                Total
                <div className="sortBy_arrows">
                  <FaArrowDown
                    fill={sortBy["encours"] == "DESC" ? "#18a0fb" : "#18a0fb50"}
                  />
                  <FaArrowUp
                    fill={sortBy["encours"] == "ASC" ? "#18a0fb" : "#18a0fb50"}
                  />
                </div>
              </div>
            ),
            accessor: (row) => row["data"]["Encours"],
            Cell: ({ value, row: { index }, column: { id } }) =>
              value && (
                <div className="cell_container">
                  <NumericFormat
                    value={value["total"]?.toFixed(2)}
                    displayType={"text"}
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    suffix=" €"
                  />
                </div>
              ),
          },
          {
            id: "up90Days",
            Header: _("up90Days"),
            accessor: (row) => row["data"]["Encours"],
            Cell: ({ value, row: { index }, column: { id } }) =>
              value && (
                <div className="cell_container">
                  <NumericFormat
                    value={value["subTotals"]["up90Days"]?.toFixed(2)}
                    displayType={"text"}
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    suffix=" €"
                  />
                </div>
              ),
          },
          {
            id: "up60Days",
            Header: _("up60Days"),
            accessor: (row) => row["data"]["Encours"],
            Cell: ({ value, row: { index }, column: { id } }) =>
              value && (
                <div className="cell_container">
                  <NumericFormat
                    value={value["subTotals"]["up60Days"]?.toFixed(2)}
                    displayType={"text"}
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    suffix=" €"
                  />
                </div>
              ),
          },
          {
            id: "up30Days",
            Header: _("up30Days"),
            accessor: (row) => row["data"]["Encours"],
            Cell: ({ value, row: { index }, column: { id } }) =>
              value && (
                <div className="cell_container">
                  <NumericFormat
                    value={value["subTotals"]["up30Days"]?.toFixed(2)}
                    displayType={"text"}
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    suffix=" €"
                  />
                </div>
              ),
          },
          {
            id: "under30Days",
            Header: _("under30Days"),
            accessor: (row) => row["data"]["Encours"],
            Cell: ({ value, row: { index }, column: { id } }) =>
              value && (
                <div className="cell_container">
                  <NumericFormat
                    value={value["subTotals"]["under30Days"].toFixed(2)}
                    displayType={"text"}
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    suffix=" €"
                  />
                </div>
              ),
          },
        ],
      },

      // {
      //   id: "encours",
      //   Header: (
      //     <div className="col" onClick={() => handleSort("encours")}>
      //       Encours
      //       <div className="sortBy_arrows">
      //         <FaArrowDown
      //           fill={sortBy["encours"] == "DESC" ? "#18a0fb" : "#18a0fb50"}
      //         />
      //         <FaArrowUp
      //           fill={sortBy["encours"] == "ASC" ? "#18a0fb" : "#18a0fb50"}
      //         />
      //       </div>
      //     </div>
      //   ),
      //   accessor: (row) => row["data"]["Encours"],
      //   Cell: ({ value }) =>
      //     value && (
      //       <div className="cell_container">
      //         <NumericFormat
      //           value={value["total"].toFixed(2)}
      //           displayType={"text"}
      //           thousandSeparator={"."}
      //           decimalSeparator={","}
      //           suffix=" €"
      //         />
      //       </div>
      //     ),
      // },
    ],
    [filter.year, sortBy]
  );

  const { getTableProps, headerGroups, prepareRow } = useTable(
    {
      columns,
      data: sortedData?.result || [],
      initialState: { pageIndex: paginationPage, pageSize: pageSize },
      manualPagination: true,
      pageCount: Math.ceil(nbResults / paginationPage),
    },
    usePagination
  );

  const { rows } = useTable(
    {
      columns,
      data: sortedData?.result || [],
    },
    usePagination
  );

  return (
    <div className="ttp-tab-container" style={{ padding: "10px 0", margin: 0 }}>
      <div className="tree_table_container">
        <table {...getTableProps()} className="tree_table">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()} style={{}}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr style={{ height: "72px" }} {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
        </table>
      </div>
      <TTPPaginator
        onPageChange={({ selected }) => {
          const newPage = selected + 1;

          if (paginationPage !== newPage) {
            setFilter((prev) => ({
              ...prev,
              page: newPage,
            }));
          }
        }}
        pageSize={pageSize}
        nbResult={nbResults}
        paginationPage={paginationPage}
      />
    </div>
  );
}
